import { create } from 'zustand';

export type Tokens = { accessToken: string; refreshToken: string };

/**
 * Sunucunun jeton yanıtı (TokenPairDTO). Oturum bağlamının TEK kaynağı budur:
 * giriş, sessiz yenileme ve kurum değiştirme aynı gövdeyi döndürür.
 */
export type TokenResponse = {
  accessToken: string;
  refreshToken: string;
  activeTenantId: string | null;
  hasMultipleActiveTenants: boolean;
};

type SessionState = {
  tokens: Tokens | null;
  tenantId: string | null;
  /** Seçilecek BAŞKA kurum var mı — sunucu söyler, panel listeyi çekip saymaz. */
  hasMultipleTenants: boolean;
  permissions: string[];
  /** Jeton yanıtını oturuma uygular: jetonlar, kurum ve çokluk bilgisi BİRLİKTE. */
  applySession: (response: TokenResponse) => void;
  setPermissions: (permissions: string[]) => void;
  clear: () => void;
};

const STORAGE_KEY = 'seoai.session';
const LAST_TENANT_KEY = 'seoai.lastTenant';

/**
 * En son çalışılan kurum. Oturumdan AYRI saklanır: çıkış yapıldığında token'lar
 * silinir ama tercih kalır, böylece kullanıcı tekrar girdiğinde aynı kurumla
 * devam eder ve seçim ekranını yeniden görmez.
 */
export function rememberTenant(tenantId: string): void {
  try {
    localStorage.setItem(LAST_TENANT_KEY, tenantId);
  } catch {
    // Yazamamak akışı durdurmaz; yalnızca hatırlama kaybolur.
  }
}

export function lastTenant(): string | null {
  try {
    return localStorage.getItem(LAST_TENANT_KEY);
  } catch {
    return null;
  }
}

type PersistedSession = Pick<SessionState, 'tokens' | 'tenantId' | 'hasMultipleTenants'>;

const EMPTY: PersistedSession = { tokens: null, tenantId: null, hasMultipleTenants: false };

function load(): PersistedSession {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return EMPTY;

    const { tokens, tenantId, hasMultipleTenants } = parsed as Partial<SessionState>;

    return {
      tokens: tokens?.accessToken && tokens.refreshToken ? tokens : null,
      tenantId: typeof tenantId === 'string' ? tenantId : null,
      /*
       * Eksikse false: bu alandan ÖNCE yazılmış bir oturumda kurum değiştirici
       * bir süre görünmez, ilk sessiz yenilemede (en geç 15 dk) yerine oturur.
       * Ters varsayım daha kötü olurdu: olmayan seçenek için liste çekilirdi.
       */
      hasMultipleTenants: hasMultipleTenants === true,
    };
  } catch {
    // Bozuk/erişilemez depolama oturumu ÇÖKERTMEZ: kullanıcı yeniden giriş yapar.
    return EMPTY;
  }
}

function persist(state: PersistedSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Kota dolu ya da gizli sekme: yazamamak akışı durdurmamalı.
  }
}

/**
 * Oturum durumu. Token'lar localStorage'da tutulur.
 *
 * NOT (bilinçli ve geçici): XSS'e karşı en güvenli yer HttpOnly çerezdir, ancak
 * backend şu an Bearer token döndürüyor (SEOAI-16) ve çerez tabanlı akış ayrı
 * bir karardır. Riski küçülten şey access token'ın 15 dakika yaşamasıdır.
 * Çerez akışına geçilirse burası ve api/client.ts değişir, çağıranlar değişmez.
 */
export const useSession = create<SessionState>((set, get) => ({
  ...load(),
  permissions: [],

  /*
   * TEK kapı (SEOAI-80 incelemesi). Önce jetonları ve kurumu ayrı ayrı yazan iki
   * eylem vardı; üç çağrı yerinin her birinin ikisini de çağırması gerekiyordu
   * ve biri unutulduğunda panel sunucunun verdiği bağlamı sessizce kaybediyordu.
   * Yanıt bir bütün olarak uygulanır.
   */
  applySession: (response) => {
    const tokens = { accessToken: response.accessToken, refreshToken: response.refreshToken };
    const tenantId = response.activeTenantId;
    const next = { tokens, tenantId, hasMultipleTenants: response.hasMultipleActiveTenants };

    set({
      ...next,
      /*
       * İzinler ANINDA düşer (SEOAI-47 incelemesi). İzinler KURUMA aittir; kurum
       * değişince eskiler bir an bile geçerli sayılmamalı. Kurum AYNIYSA
       * korunur: sessiz yenileme her 15 dakikada bir menüyü söndürmemeli.
       */
      permissions: tenantId === get().tenantId ? get().permissions : [],
    });

    persist(next);
    if (tenantId !== null) rememberTenant(tenantId);
  },

  setPermissions: (permissions) => set({ permissions }),

  clear: () => {
    set({ tokens: null, tenantId: null, hasMultipleTenants: false, permissions: [] });
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Yok sayılır; bellekteki durum zaten temizlendi.
    }
  },
}));

/** Store dışından (API istemcisi) okuma/yazma — React ağacına bağlı değil. */
export const sessionStore = {
  tokens: (): Tokens | null => useSession.getState().tokens,
  applySession: (response: TokenResponse): void => useSession.getState().applySession(response),
  clear: (): void => useSession.getState().clear(),
};
